import { AppIcon } from '@/components/AppIcon';
import { formatDateTime } from '@/format';
import {
  isNew,
  liveSince,
  liveUntil,
  postedAgo,
  PRIORITY_LABEL,
  type FeedItem,
  type Priority,
} from './announcementFeed';

/**
 * The small pieces every view of an announcement shares, so the board, the
 * pop-up and the composer's preview say "Urgent" or "2 hours ago" the same way.
 */

/** "⚠ Urgent" or "Important", in words as well as colour. Nothing for an ordinary notice. */
export function PriorityTag({ priority }: { priority: Priority }) {
  const label = PRIORITY_LABEL[priority];
  if (!label) return null;
  return (
    <span className={`noticetag noticetag--${priority.toLowerCase()}`}>
      {priority === 'URGENT' && <AppIcon kind="attention" size={13} />}
      {label}
    </span>
  );
}

/** "New · Rehan Dogar · 2 hours ago · Ends tomorrow", leaving out whatever is unknown. */
export function NoticeMeta({
  item,
  author,
  now,
}: {
  item: FeedItem;
  author: string | null;
  now: number;
}) {
  const since = liveSince(item);
  const ago = postedAgo(since, now);
  const until = liveUntil(item, now);
  const fresh = isNew(item, now);
  if (!fresh && !author && !ago && !until) return null;

  // Every part carries its own "·" in front; the row is pulled back by one
  // separator's width and clipped, so the one at the start of each line,
  // wrapped or not, is cut off rather than left dangling. Each part's words
  // sit in <bdi>, so "6 hours ago" stays in that order on an Urdu page and an
  // Urdu name reads right on an English one.
  return (
    <p className="noticemeta">
      <span className="noticemeta__row">
        {fresh && (
          <span className="noticemeta__part">
            <span className="noticetag noticetag--new">New</span>
          </span>
        )}
        {author && (
          <span className="noticemeta__part">
            <bdi>{author}</bdi>
          </span>
        )}
        {since && ago && (
          <time className="noticemeta__part" dateTime={since.toISOString()} title={formatDateTime(since)}>
            <bdi>{ago}</bdi>
          </time>
        )}
        {until && (
          <span className="noticemeta__part">
            <bdi>{until}</bdi>
          </span>
        )}
      </span>
    </p>
  );
}
